import React, { useState, useEffect, useMemo } from 'react';
import { API_URL } from '../config';

const ISSUERS = [
  { id: 'AMERICAN_EXPRESS', name: 'American Express' },
  { id: 'CHASE', name: 'Chase' },
  { id: 'CITI', name: 'Citi' },
  { id: 'CAPITAL_ONE', name: 'Capital One' },
  { id: 'BANK_OF_AMERICA', name: 'Bank of America' },
  { id: 'WELLS_FARGO', name: 'Wells Fargo' },
  { id: 'DISCOVER', name: 'Discover' },
  { id: 'US_BANK', name: 'US Bank' },
  { id: 'BARCLAYS', name: 'Barclays' },
];

function AddCardModal({ onClose, onAdd }) {
  const [step, setStep] = useState(1);
  const [selectedIssuer, setSelectedIssuer] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCard, setSelectedCard] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (selectedIssuer) {
      fetchCardsForIssuer(selectedIssuer.id);
    }
  }, [selectedIssuer]);

  const fetchCardsForIssuer = async (issuerId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/cards/search?issuer=${issuerId}`);
      const data = await response.json();
      if (data.cards && Array.isArray(data.cards)) {
        const sortedCards = data.cards.sort((a, b) =>
          (a.cardName || a.name || '').localeCompare(b.cardName || b.name || '')
        );
        setCards(sortedCards);
      } else {
        setCards([]);
      }
    } catch (err) {
      console.error('Error fetching cards:', err);
      setError('Failed to load cards. Please try again.');
      setCards([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredCards = useMemo(() => {
    if (!searchQuery.trim()) return cards;
    const query = searchQuery.toLowerCase();
    return cards.filter((card) => {
      const name = (card.cardName || card.name || '').toLowerCase();
      const network = (card.network || '').toLowerCase();
      return name.includes(query) || network.includes(query);
    });
  }, [cards, searchQuery]);

  const handleIssuerSelect = (issuer) => {
    setSelectedIssuer(issuer);
    setStep(2);
    setSearchQuery('');
    setSelectedCard(null);
  };

  const handleBack = () => {
    setStep(1);
    setSelectedIssuer(null);
    setCards([]);
    setSearchQuery('');
    setSelectedCard(null);
  };

  const handleSubmit = () => {
    if (!selectedCard) {
      alert('Please select a card');
      return;
    }
    const cardData = {
      cardName: selectedCard.cardName || selectedCard.name,
      network: selectedCard.network || selectedCard._raw?.network || 'Unknown',
      bank: selectedIssuer.name,
      bankId: selectedIssuer.id,
      imageUrl: selectedCard.imageUrl || selectedCard._raw?.imageUrl,
      cardUrl: selectedCard.cardUrl || selectedCard._raw?.url,
      annualFee: selectedCard.annualFee || selectedCard._raw?.annualFee,
      signupBonus: selectedCard.signupBonus,
      _cardData: selectedCard._raw || selectedCard,
    };
    onAdd(cardData);
  };

  const handleAddGenericCard = () => {
    const cardData = {
      cardName: `${selectedIssuer.name} Card`,
      network: 'Unknown',
      bank: selectedIssuer.name,
      bankId: selectedIssuer.id,
      isGeneric: true,
    };
    onAdd(cardData);
  };

  const formatAnnualFee = (fee) => {
    if (fee === undefined || fee === null) return null;
    if (typeof fee === 'string') return fee;
    return fee === 0 ? 'No annual fee' : `$${fee}/year`;
  };

  const formatSignupBonus = (bonus) => {
    if (!bonus) return null;
    if (typeof bonus === 'string') return bonus;
    if (bonus.description) return bonus.description;
    if (bonus.amount) return `${bonus.amount.toLocaleString()} ${bonus.currency || 'points'}`;
    return null;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md max-h-[85vh] bg-white rounded-t-3xl sm:rounded-3xl flex flex-col overflow-hidden shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-outline-variant/15">
          <div className="flex items-center gap-3">
            {step === 2 && (
              <button onClick={handleBack} className="text-on-surface-variant hover:text-on-surface transition-colors">
                <span className="material-symbols-outlined">arrow_back</span>
              </button>
            )}
            <h2 className="text-lg font-semibold text-on-surface">
              {step === 1 ? 'Select Card Issuer' : `${selectedIssuer?.name} Cards`}
            </h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-surface-container transition-colors">
            <span className="material-symbols-outlined text-on-surface-variant">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {step === 1 && (
            <div className="space-y-2">
              {ISSUERS.map((issuer) => (
                <button
                  key={issuer.id}
                  className="w-full flex items-center justify-between p-4 bg-surface-container-low rounded-2xl hover:bg-surface-container transition-colors text-left"
                  onClick={() => handleIssuerSelect(issuer)}
                >
                  <span className="text-sm font-medium text-on-surface">{issuer.name}</span>
                  <span className="material-symbols-outlined text-on-surface-variant text-lg">chevron_right</span>
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <>
              <div className="sticky top-0 z-10 bg-white pb-3">
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-surface-container-low border border-outline-variant/40 rounded-2xl text-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  placeholder="Search cards..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
              </div>

              {loading && (
                <div className="flex flex-col items-center justify-center py-8 gap-3">
                  <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
                  <p className="text-sm text-on-surface-variant">Loading cards...</p>
                </div>
              )}

              {error && (
                <div className="text-center py-6">
                  <p className="text-sm text-error mb-3">{error}</p>
                  <button
                    onClick={() => fetchCardsForIssuer(selectedIssuer.id)}
                    className="px-4 py-2 text-sm font-medium text-primary bg-primary-light rounded-full"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {!loading && !error && (
                <div className="space-y-2">
                  {filteredCards.length === 0 && searchQuery && (
                    <p className="text-center text-sm text-on-surface-variant py-6">
                      No cards match "{searchQuery}"
                    </p>
                  )}

                  {filteredCards.map((card, index) => {
                    const cardName = card.cardName || card.name || 'Unknown Card';
                    const annualFee = formatAnnualFee(card.annualFee || card._raw?.annualFee);
                    const bonus = formatSignupBonus(card.signupBonus);
                    const isSelected = selectedCard === card;

                    return (
                      <button
                        key={card.cardId || card.id || index}
                        className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${
                          isSelected
                            ? 'border-primary bg-primary-light/30'
                            : 'border-transparent bg-surface-container-low hover:bg-surface-container'
                        }`}
                        onClick={() => setSelectedCard(card)}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-on-surface">{cardName}</span>
                          {isSelected && (
                            <span className="material-symbols-outlined filled text-primary text-lg">check_circle</span>
                          )}
                        </div>
                        {annualFee && (
                          <span className="text-xs text-on-surface-variant">{annualFee}</span>
                        )}
                        {bonus && (
                          <p className="text-xs text-primary mt-1">
                            <span className="font-medium">Bonus:</span> {bonus}
                          </p>
                        )}
                      </button>
                    );
                  })}

                  <button
                    className="w-full text-left p-4 rounded-2xl border-2 border-dashed border-outline-variant hover:border-primary/50 transition-colors"
                    onClick={handleAddGenericCard}
                  >
                    <span className="text-sm font-medium text-on-surface-variant">
                      Other {selectedIssuer?.name} Card
                    </span>
                    <p className="text-xs text-on-surface-variant/70">Not listed above</p>
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {step === 2 && selectedCard && (
          <div className="p-4 border-t border-outline-variant/15">
            <button
              className="w-full py-3.5 bg-gradient-to-r from-primary via-primary to-primary-dark text-on-primary rounded-2xl text-sm font-semibold hover:shadow-md active:scale-[0.97] duration-200 transition-all"
              onClick={handleSubmit}
            >
              Add {selectedCard.cardName || selectedCard.name}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AddCardModal;
